
import React from "react";
import { Button } from "@/components/ui/button";
import { Edit, Trash2, Eye, RefreshCw, Power, Shield } from "lucide-react";
import { StrategyWithDetails } from "@/types/strategy-types";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface StrategyActionsProps {
  strategy: StrategyWithDetails;
  onToggleStatus: (strategyId: string, currentStatus: boolean) => Promise<void>;
  onToggleActive: (strategyId: string, currentActive: boolean) => Promise<void>;
  onApproveStrategy: (strategyId: string, approved: boolean) => Promise<void>;
  onEdit: (strategy: StrategyWithDetails) => void;
  onDelete: (strategyId: string) => void;
  onPreview: (strategy: StrategyWithDetails) => void;
  userRole?: string | null;
}

const StrategyActions = ({
  strategy,
  onToggleStatus,
  onToggleActive,
  onApproveStrategy,
  onEdit,
  onDelete,
  onPreview,
  userRole
}: StrategyActionsProps) => {
  const isPro = strategy.plan === 'pro';
  const isActive = strategy.active;
  const isApproved = strategy.approved;
  
  const handlePreview = () => {
    onPreview(strategy);
  };
  
  const handleEdit = () => {
    onEdit(strategy);
  };
  
  const handleToggleStatus = () => {
    onToggleStatus(strategy.id, isPro);
  };
  
  const handleToggleActive = () => {
    onToggleActive(strategy.id, isActive);
  };
  
  const handleApprove = () => {
    onApproveStrategy(strategy.id, !isApproved);
  };
  
  const handleDelete = () => {
    onDelete(strategy.id);
  };
  
  return (
    <div className="flex items-center justify-end space-x-1">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" onClick={handlePreview} className="h-8 w-8">
              <Eye className="h-4 w-4" />
              <span className="sr-only">Preview</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Preview strategy</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" onClick={handleEdit} className="h-8 w-8">
              <Edit className="h-4 w-4" />
              <span className="sr-only">Edit</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Edit strategy</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" onClick={handleToggleStatus} className="h-8 w-8">
              <RefreshCw className="h-4 w-4" />
              <span className="sr-only">Toggle Plan</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{isPro ? 'Change to Free' : 'Change to Pro'}</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" onClick={handleToggleActive} className="h-8 w-8">
              <Power className={`h-4 w-4 ${isActive ? 'text-green-500' : 'text-gray-400'}`} />
              <span className="sr-only">Toggle Active</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{isActive ? 'Deactivate' : 'Activate'}</p>
          </TooltipContent>
        </Tooltip>
        
        {userRole === 'admin' && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" onClick={handleApprove} className="h-8 w-8">
                <Shield className={`h-4 w-4 ${isApproved ? 'text-green-500' : 'text-yellow-500'}`} />
                <span className="sr-only">Approve</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{isApproved ? 'Unapprove' : 'Approve'}</p>
            </TooltipContent>
          </Tooltip>
        )}
        
        <Tooltip>
          <TooltipTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handleDelete}
              className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
              <span className="sr-only">Delete</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Delete strategy</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

export default StrategyActions;
